/**
 * Darwin — the coach: a text Q&A grounded in the user's own session data.
 *
 * The name goes with the product's character: "Erectus" evolves from
 * knuckle-walker to upright across five stages, and Darwin is who reads the
 * evidence and tells you what to do next. It also reads as a person's name,
 * which is the point — this should feel like a coach you can ask, not a form.
 *
 * Framing, deliberately: this is a **coach informed by physical-therapy
 * practice**, never "your physical therapist". docs/LEGAL_PRIVACY.md §0 keeps
 * this product out of the medical track, and §7 flags that a PT-branded feature
 * reads as forming a clinician–patient relationship — which is regulated per
 * US state. So the persona is an experienced practitioner's *knowledge*, with a
 * standing instruction to send anything specific to a real clinician.
 */
/** Daily allowance, reset at local midnight (see `quotaDayKey`). */
/** Display name of the persona. Code identifiers stay generic ("coach") so
 *  this can be renamed without touching the data model. */
export const COACH_NAME = 'Darwin';
export const COACH_TAGLINE = 'Your posture coach';
/**
 * The line that must appear wherever Darwin speaks.
 * docs/LEGAL_PRIVACY.md §0 keeps this product out of the medical track, and §7
 * flags that a PT-branded feature reads as forming a clinician relationship.
 */
export const COACH_DISCLAIMER = 'Darwin is a coach, not your physical therapist. General information based on your own ' +
    'session data — not a diagnosis, and not a substitute for someone who can examine you.';
export const COACH_DAILY_LIMIT = 10;
/** At most this many exercises in any one recommendation — more is a list to choose from, not advice. */
export const MAX_RECOMMENDED_EXERCISES = 3;
export const COACH_MAX_QUESTION_CHARS = 600;
export const COACH_MAX_ANSWER_CHARS = 1400;
/** How many earlier turns are replayed to the model. */
export const COACH_HISTORY_TURNS = 6;
/**
 * Query parameter that opens one exercise directly.
 * Darwin recommends specific movements, and "go to the library and find it
 * yourself" loses the recommendation halfway.
 */
export const EXERCISE_DEEP_LINK_PARAM = 'exercise';
export function exerciseLink(id) {
    return `/exercises?${EXERCISE_DEEP_LINK_PARAM}=${encodeURIComponent(id)}`;
}
/** The prompts offered as buttons, so a blank box is never the only option. */
export const COACH_SUGGESTIONS = [
    {
        topic: 'weekly-review',
        label: 'Review my last 7 days',
        question: 'Review my last 7 days of sessions. What changed, and what should I focus on?',
    },
    {
        topic: 'trend',
        label: 'How is my posture trending?',
        question: 'How is my neck angle and trunk lean trending over the sessions you can see? Is the change real or within the measurement margin?',
    },
    {
        topic: 'exercises',
        label: 'What should I work on?',
        question: 'Based on my sessions, which exercises from the library should I prioritise this week, and how often?',
    },
];
/**
 * Day key in the user's own timezone.
 *
 * A UTC day would reset at 9am in Seoul and 8pm in California — the allowance
 * has to line up with the user's sense of "today".
 */
export function quotaDayKey(timestamp, timeZone) {
    try {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(new Date(timestamp));
    }
    catch {
        return new Date(timestamp).toISOString().slice(0, 10);
    }
}
/** Epoch ms of the next local midnight, for "resets in 4h" style copy. */
export function nextResetAt(timestamp, timeZone) {
    const hourMs = 60 * 60 * 1000;
    const today = quotaDayKey(timestamp, timeZone);
    // Walk forward in hours until the local day flips; cheap, and correct for
    // half-hour offsets and DST without a date library.
    for (let i = 1; i <= 48; i += 1) {
        const probe = timestamp + i * hourMs;
        if (quotaDayKey(probe, timeZone) !== today) {
            // Round back to the start of that hour for a tidy number.
            return probe - (probe % hourMs);
        }
    }
    return timestamp + 24 * hourMs;
}
export function remainingQuota(quota) {
    return Math.max(0, quota.limit - quota.used);
}
//# sourceMappingURL=coach.js.map